import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { Callout, Container, Jumbo, Separator } from '@treams/treams-ui';
import {
	BellIcon,
	ConversationIcon,
	DialogueIcon,
	FriendsFeminineMasculineIcon,
	GridIcon,
	GroupsIcon,
	HammerIcon,
	HeartRateIcon,
	HistoryIcon,
	LibraryIcon,
	NetworkIcon,
	PaintBrushWideIcon,
	PopOutIcon,
	PuzzleIcon,
	SuitcaseIcon,
	TargetIcon,
} from '@treams/treams-icons';
import useFeature from 'packages/kakapo-ui-application/hooks/useFeature';

import OverviewSection from './OverviewSection';

const Background = styled(Box)`
	height: 100%;
	background: ${(props) => props.theme.colors.whiteBlue};
`;

export default function OverviewSettings() {
	const { t } = useTranslation();

	const hasCycles = useFeature('2021.cycles');
	const hasLearning = useFeature('2021.learning');
	const hasFeedback = useFeature('2021.feedback');
	const hasConversations = useFeature('2021.evaluations');
	const hasTalentReviews = useFeature('2021.talentReviews');
	const hasEngagement = useFeature('2022.engagement');

	return (
		<>
			<Jumbo
				title={t('settings.overview.title')}
				description={t('settings.overview.description')}
			/>
			<Separator />
			<Background>
				<Container p={4}>
					<Box maxWidth={240 * 3 + 16}>
						<Box mb={4}>
							<Callout color="blue">
								<Box>
									<strong>{t('settings.overview.callout.new')}:</strong>{' '}
									{t('settings.overview.callout.description')}
								</Box>
							</Callout>
						</Box>
						<Box mb={5}>
							<OverviewSection
								title={t('settings.overview.organization.title')}
								description={t('settings.overview.organization.description')}
								color="manager"
								items={[
									{
										icon: <FriendsFeminineMasculineIcon />,
										link: '/settings/employees',
										title: t('settings.organizations.employees.title'),
									},
									{
										icon: <NetworkIcon />,
										link: '/settings/organization/departments',
										title: t('settings.organizations.departments.title'),
									},
									{
										icon: <GroupsIcon />,
										link: '/settings/organization/teams',
										title: t('settings.organizations.teams.title'),
									},
									{
										icon: <PaintBrushWideIcon />,
										link: '/settings/organization/theme',
										title: t('settings.organizations.theme.title'),
									},
									{
										icon: <PuzzleIcon />,
										link: '/settings/integrations',
										title: t('settings.integrations.title'),
									},
									{
										icon: <TargetIcon />,
										link: '/settings/organization/drivers',
										title: t('settings.organizations.drivers.title'),
									},
									{
										icon: <PopOutIcon />,
										title: t('settings.superuser.training'),
										link: t('settings.superuser.trainingLink'),
									},
								]}
							/>
						</Box>
						<Box mb={5}>
							<OverviewSection
								title={t('settings.overview.general.title')}
								description={t('settings.overview.general.description')}
								color="employee"
								items={[
									{
										icon: <SuitcaseIcon />,
										link: '/settings/jobs',
										title: t('settings.jobs.title'),
									},
									{
										icon: <HistoryIcon />,
										link: '/settings/cycles',
										title: t('settings.cycles.title'),
										enabled: hasCycles,
									},
									{
										icon: <BellIcon />,
										link: '/settings/notifications',
										title: t('settings.notifications.title'),
									},
								]}
							/>
						</Box>
						<OverviewSection
							title={t('settings.overview.content.title')}
							description={t('settings.overview.content.description')}
							color="purple"
							items={[
								{
									icon: <HammerIcon />,
									link: '/settings/competencies',
									title: t('settings.competencies.title'),
								},
								{
									icon: <DialogueIcon />,
									link: '/settings/feedback-v2',
									title: t('settings.feedback.title'),
									enabled: hasFeedback,
								},
								{
									icon: <ConversationIcon />,
									link: '/settings/conversations',
									title: t('settings.conversations.title'),
									enabled: hasConversations,
								},
								{
									icon: <GridIcon />,
									link: '/settings/talent-reviews',
									title: t('settings.talentReviews.title'),
									enabled: hasTalentReviews,
								},
								{
									icon: <LibraryIcon />,
									link: '/settings/learning',
									title: t('settings.learning.title'),
									enabled: hasLearning,
								},
								{
									icon: <HeartRateIcon />,
									link: '/settings/engagement',
									title: t('settings.engagement.title'),
									enabled: hasEngagement,
								},
							]}
						/>
					</Box>
				</Container>
			</Background>
		</>
	);
}
